import { useEffect, useState } from 'react';
import api from '../../services/api';
import Button from './Button';
import toast from 'react-hot-toast';

const INP = 'w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none';

export default function SecuritySection({ readOnlyEmail = false }) {
  const [info, setInfo]       = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState({ email: '', password: '', confirm: '', current_password: '' });
  const sf = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    api.get('/auth/me').then((r) => {
      const d = r.data.data;
      setInfo({ email: d.email || '', has_password: !!d.has_password });
    }).catch(() => {});
  }, []);

  function cancelEdit() {
    setEditing(false);
    setForm({ email: '', password: '', confirm: '', current_password: '' });
  }

  async function handleSave() {
    const emailToSend = readOnlyEmail ? null : form.email;
    if (!emailToSend && !form.password) return toast.error('Enter an email or new password to update');
    if (form.password && form.password.length < 8) return toast.error('Password must be at least 8 characters');
    if (form.password && form.password !== form.confirm) return toast.error('Passwords do not match');
    setSaving(true);
    try {
      await api.post('/auth/set-password', {
        ...(emailToSend           ? { email: emailToSend }                    : {}),
        ...(form.password         ? { password: form.password }               : {}),
        ...(form.current_password ? { current_password: form.current_password } : {}),
      });
      toast.success('Login credentials updated');
      const r = await api.get('/auth/me');
      const d = r.data.data;
      setInfo({ email: d.email || '', has_password: !!d.has_password });
      cancelEdit();
    } catch {}
    finally { setSaving(false); }
  }

  if (!info) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 text-sm">Login &amp; Security</h3>
        {!editing && (
          <Button size="sm" variant="secondary" onClick={() => {
            setForm((p) => ({ ...p, email: info.email || '' }));
            setEditing(true);
          }}>
            {info.has_password ? 'Update' : 'Set Up'}
          </Button>
        )}
      </div>

      {!editing ? (
        <div className="space-y-3 text-sm">
          <div>
            <p className="text-xs text-gray-400">Email</p>
            <p className="font-medium text-gray-900 mt-0.5">{info.email || <span className="text-gray-400 font-normal">Not set</span>}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Password</p>
            <p className="font-medium text-gray-900 mt-0.5">{info.has_password ? '••••••••' : <span className="text-gray-400 font-normal">Not set</span>}</p>
          </div>
          {!info.has_password && (
            <p className="text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
              Set an email &amp; password to enable login without OTP.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {info.has_password && (
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Current Password <span className="text-red-500">*</span></label>
              <input type="password" value={form.current_password} onChange={sf('current_password')} className={INP} placeholder="Required to save changes" />
            </div>
          )}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">Email Address</label>
            <input
              type="email"
              value={form.email}
              onChange={readOnlyEmail ? undefined : sf('email')}
              readOnly={readOnlyEmail}
              className={`${INP} ${readOnlyEmail ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`}
              placeholder={info.email || 'yourname@example.com'}
            />
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">{info.has_password ? 'New Password' : 'Password'}</label>
            <input type="password" value={form.password} onChange={sf('password')} className={INP} placeholder="Min 8 characters" />
          </div>
          {form.password && (
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
              <input type="password" value={form.confirm} onChange={sf('confirm')} className={INP} placeholder="Repeat password" />
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <Button size="sm" variant="secondary" onClick={cancelEdit}>Cancel</Button>
            <Button size="sm" fullWidth loading={saving} onClick={handleSave}>Save</Button>
          </div>
        </div>
      )}
    </div>
  );
}
